import { motion } from "framer-motion";
import { DOWNLOAD_LINKS } from "@/data/content";
import { useLang } from "@/i18n/LanguageContext";

const AppleIcon = () => (
    <svg viewBox="0 0 24 24" className="w-7 h-7" fill="currentColor" aria-hidden="true">
        <path d="M17.05 12.74c-.02-2.86 2.34-4.23 2.44-4.3-1.33-1.94-3.4-2.21-4.14-2.24-1.76-.18-3.43 1.04-4.32 1.04-.9 0-2.27-1.01-3.74-.98-1.92.03-3.69 1.12-4.68 2.83-2 3.46-.51 8.59 1.43 11.41.95 1.38 2.08 2.93 3.55 2.87 1.42-.06 1.96-.92 3.68-.92 1.71 0 2.21.92 3.71.89 1.53-.03 2.5-1.4 3.43-2.79 1.08-1.6 1.53-3.15 1.55-3.23-.03-.01-2.97-1.14-3-4.58zM14.34 4.36c.78-.95 1.31-2.27 1.16-3.58-1.13.05-2.49.75-3.3 1.7-.72.83-1.36 2.18-1.19 3.47 1.26.1 2.55-.64 3.33-1.59z" />
    </svg>
);

const GooglePlayIcon = () => (
    <svg viewBox="0 0 512 512" className="w-7 h-7" aria-hidden="true">
        <path fill="#FFC107" d="M433 256c0-8.6-4.6-16.5-12.1-20.8L153.2 78.2c-4.9-2.9-10-3.9-14.8-3.4l176 181.2z" />
        <path fill="#FF3D00" d="M138.4 74.8c-3.2.3-6.2 1.4-9 3.1-7.8 4.7-12.4 13-12.4 22.1V412c0 9.1 4.6 17.4 12.4 22.1 2.8 1.7 5.8 2.8 9 3.1l176-181.2z" />
        <path fill="#4CAF50" d="M314.4 256 138.4 437.2c4.8.5 9.9-.5 14.8-3.4L420.9 276.8c7.5-4.3 12.1-12.2 12.1-20.8z" />
        <path fill="#1976D2" d="M138.4 74.8 314.4 256l106.5-105.2c-1.5-.9-3.2-1.7-5-2.6L153.2 78.2c-4.9-2.9-10-3.9-14.8-3.4z" />
    </svg>
);

const HuaweiIcon = () => (
    <svg viewBox="0 0 24 24" className="w-7 h-7" fill="currentColor" aria-hidden="true">
        <path d="M12 2C7.5 3.5 4 6 4 11c0 5 3.5 9 8 11 4.5-2 8-6 8-11 0-5-3.5-7.5-8-9zm0 4c.6 1.5 1.5 3 3 4 0 2.5-1 5-3 7-2-2-3-4.5-3-7 1.5-1 2.4-2.5 3-4z" />
    </svg>
);

export const DownloadButtons = ({ testIdPrefix = "" }) => {
    const { t } = useLang();
    const buttons = [
        { id: "apple", label: "App Store", href: DOWNLOAD_LINKS.apple, testId: "download-apple", icon: <AppleIcon /> },
        { id: "google", label: "Google Play", href: DOWNLOAD_LINKS.google, testId: "download-google", icon: <GooglePlayIcon /> },
        { id: "huawei", label: "AppGallery", href: DOWNLOAD_LINKS.huawei, testId: "download-huawei", icon: <HuaweiIcon /> },
    ];

    return (
        <div
            data-testid={`${testIdPrefix}download-buttons`}
            className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4"
        >
            {buttons.map((b, i) => (
                <motion.a
                    key={b.id}
                    href={b.href}
                    data-testid={`${testIdPrefix}${b.testId}`}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 * i, duration: 0.4 }}
                    whileHover={{ y: -3, scale: 1.02 }}
                    className={`group flex items-center gap-3 px-5 py-3 rounded-full border transition-all ${
                        b.id === "apple"
                            ? "bg-[var(--primary)] text-black border-[var(--primary)] hover:bg-[var(--primary-hover)]"
                            : "bg-black/40 text-white border-white/15 hover:border-[var(--primary)] hover:bg-[var(--primary)]/10"
                    }`}
                >
                    <span className="shrink-0">{b.icon}</span>
                    <span className="text-start leading-tight">
                        <span className="block text-[10px] uppercase tracking-widest opacity-80">
                            {t.download.from}
                        </span>
                        <span className="block font-bold text-base">{b.label}</span>
                    </span>
                </motion.a>
            ))}
        </div>
    );
};
